import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  User,
  Send,
  Image as ImageIcon,
  X,
  Zap,
  Download,
  Monitor,
  Smartphone,
  Globe,
  Clock,
  CheckCircle2,
  Trophy,
  Loader2,
  Sparkles,
  Film,
  Layers,
  ArrowUp,
  ArrowDown,
  Trash2
} from 'lucide-react';
import api from './services/api';
import { useTranslation } from './LanguageContext';

const Typewriter = ({ text, onComplete }) => {
  const [display, setDisplay] = useState('');
  
  useEffect(() => {
    if (!text) {
      if (onComplete) onComplete();
      return;
    }
    let i = 0;
    const timer = setInterval(() => {
      setDisplay(text.substring(0, i + 1));
      i++;
      if (i >= text.length) {
        clearInterval(timer);
        if (onComplete) onComplete();
      }
    }, 5);
    return () => clearInterval(timer);
  }, [text]);

  return <>{display}</>;
};

function CreateVideo({ onNavigate, isActive }) {
  const { t } = useTranslation();
  const [prompt, setPrompt] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [cloudinaryUrl, setCloudinaryUrl] = useState('');
  const [aspectRatio, setAspectRatio] = useState('pc');
  const [language, setLanguage] = useState('fr');
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [merging, setMerging] = useState(false);
  const [mergeProgress, setMergeProgress] = useState(0);
  const [error, setError] = useState('');
  const [mergeList, setMergeList] = useState([]);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showClearHistoryModal, setShowClearHistoryModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem('chat_history');
    if (saved) {
      return JSON.parse(saved).map(m => ({ ...m, animate: false }));
    }
    return [{
      id: 'welcome',
      type: 'bot',
      content: t('welcomeStudio'),
      animate: true
    }];
  });
  const messagesEndRef = useRef(null);
  const [pendingData, setPendingData] = useState(() => {
    const saved = localStorage.getItem('chat_pending_data');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    localStorage.setItem('chat_history', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (pendingData) {
      localStorage.setItem('chat_pending_data', JSON.stringify(pendingData));
    } else {
      localStorage.removeItem('chat_pending_data');
    }
  }, [pendingData]);

  const handleAnimationComplete = (id) => {
    setMessages(prev => prev.map(m => m.id === id ? { ...m, animate: false } : m));
  };

  // État pour suivre si l'utilisateur a fait défiler manuellement
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Gestionnaire de défilement pour détecter quand l'utilisateur fait défiler manuellement
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const atBottom = scrollHeight - scrollTop <= clientHeight + 1;
    setIsScrolledToBottom(atBottom);
  };

  useEffect(() => {
    // Ne faire défiler automatiquement que si l'utilisateur est déjà en bas
    if (isScrolledToBottom) {
      scrollToBottom();
    }
  }, [messages, loading, optimizing, previewUrl, isScrolledToBottom]);

  useEffect(() => {
    if (isActive) {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(storedUser);
      setTimeout(scrollToBottom, 100);
    }
  }, [isActive]);

  // Liste des vidéos générées dans la session courante (pour la fusion)
  const generatedVideos = messages.filter(m => m.videoUrl && !m.videoUrl.startsWith('blob:') && !m.isError && !m.isMerged);

  const handleExtendVideo = async (videoUrl) => {
    if (uploadingImage || loading) return;
    
    setUploadingImage(true);
    try {
      // Création d'un élément vidéo pour capturer la frame
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous'; // Nécessaire pour éviter le tainting du canvas
      video.src = videoUrl;
      video.muted = true;
      
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => {
          // Se positionner à la fin (moins 0.1s pour être sûr d'avoir une image)
          video.currentTime = Math.max(0, video.duration - 0.1);
        };
        video.onseeked = () => resolve();
        video.onerror = (e) => reject(new Error('Erreur chargement vidéo'));
        setTimeout(() => reject(new Error('Timeout extraction frame')), 10000);
      });
      
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob(async (blob) => {
        if (!blob) {
            setUploadingImage(false);
            return;
        }
        const uniqueName = `frame_suite_${Date.now()}_${Math.floor(Math.random() * 1000)}.jpg`;
        const file = new File([blob], uniqueName, { type: "image/jpeg" });
        setImageFile(file);
        
        const formDataImage = new FormData();
        formDataImage.append('file', file);

        try {
            const uploadRes = await fetch('https://service.ralp-ai.site/upload', {
              method: 'POST',
              body: formDataImage
            });

            if (!uploadRes.ok) throw new Error("Upload failed");

            const uploadData = await uploadRes.json();
            setPreviewUrl(`${uploadData.url}?t=${Date.now()}`);
            setCloudinaryUrl(uploadData.url);
            setPrompt(t('cv_sequel_prefix'));
            document.querySelector('.chat-textarea')?.focus();
        } catch (err) {
            setError("Erreur upload frame.");
        } finally {
            setUploadingImage(false);
        }
      }, 'image/jpeg');
      
    } catch (err) {
      console.error(err);
      setError("Impossible d'extraire la frame (CORS?).");
      setUploadingImage(false);
    }
  };

  const handleMergeVideos = () => {
    if (merging || generatedVideos.length < 2) return;

    setMergeList([...generatedVideos]);

    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'bot',
      content: t('cv_merge_title'),
      isMergeConfirmation: true,
      animate: true
    }]);
    setTimeout(scrollToBottom, 100);
  };

  const cancelMerge = () => {
    setMessages(prev => prev.map(msg => 
      msg.isMergeConfirmation ? { ...msg, isMergeConfirmation: false, content: t('cv_merge_canceled'), animate: true } : msg
    ));
  };

  const moveVideo = (index, direction) => {
    const newList = [...mergeList];
    if (direction === 'up' && index > 0) {
        [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    } else if (direction === 'down' && index < newList.length - 1) {
        [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    }
    setMergeList(newList);
  };

  const removeVideoFromMerge = (index) => {
    const newList = [...mergeList];
    newList.splice(index, 1);
    setMergeList(newList);
  };

  const executeMerge = async () => {
    setMessages(prev => prev.map(msg => 
      msg.isMergeConfirmation ? { ...msg, isMergeConfirmation: false, content: t('cv_merge_confirmed'), animate: true } : msg
    ));

    if (merging || mergeList.length < 2) return;

    setMerging(true);
    setMergeProgress(0);
    setError('');

    const mergeMsgId = `merge-status-${Date.now()}`;
    setMessages(prev => [...prev, {
        id: mergeMsgId,
        type: 'bot',
        content: t('cv_merge_prep', { count: mergeList.length }),
        isProcessing: true,
        animate: true
    }]);

    // Simulation de progression car le serveur ne renvoie pas de stream de progression
    const progressInterval = setInterval(() => {
      setMergeProgress(prev => {
        if (prev >= 90) return 90;
        return prev + 5;
      });
    }, 500);

    try {
        const videoUrls = mergeList.map(v => v.videoUrl);

        // Appel au service Python pour la fusion
        const mergeResponse = await fetch('https://service.ralp-ai.site/merge-videos', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ videos: videoUrls })
        });

        if (!mergeResponse.ok) {
            throw new Error('Erreur lors de la fusion sur le serveur Python');
        }

        const mergedBlob = await mergeResponse.blob();
        
        clearInterval(progressInterval);
        setMergeProgress(100);
        setMessages(prev => prev.map(msg => 
            msg.id === mergeMsgId ? { ...msg, content: t('cv_finalizing'), isProcessing: true, animate: true } : msg
        ));

        let url = URL.createObjectURL(mergedBlob);

        let savedToHistory = false;
        try {
            const formData = new FormData();
            formData.append('file', mergedBlob, 'merged_video.mp4');
            const response = await api.post('/video/save-merged', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            if (response.data && response.data.url) {
                url = response.data.url;
                savedToHistory = true;
            }
        } catch (saveErr) {
            console.error("Erreur sauvegarde vidéo fusionnée:", saveErr);
        }

        setMessages(prev => prev.map(msg => 
            msg.id === mergeMsgId ? { 
                ...msg, 
                content: t('cv_merge_success', { count: mergeList.length, saved: savedToHistory ? t('cv_saved_project') : '' }),
                videoUrl: url,
                isMerged: true, // Flag pour ne pas afficher "Générer une suite"
                isProcessing: false,
                animate: true
            } : msg
        ));

    } catch (err) {
        clearInterval(progressInterval);
        console.error("Erreur de fusion vidéo:", err);
        setError(t('cv_merge_error_res'));
        setMessages(prev => prev.map(msg => msg.id === mergeMsgId ? { ...msg, isError: true, content: t('cv_merge_failed'), isProcessing: false, animate: true } : msg));
    } finally {
        setMerging(false);
        setMergeProgress(0);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setPreviewUrl('');
    setCloudinaryUrl('');
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'image/jpeg') {
        setError(t('cv_invalid_format'));
        return;
      }
      setError('');
      setImageFile(file);
      setUploadingImage(true);

      try {
        const formDataImage = new FormData();
        formDataImage.append('file', file);

        const uploadRes = await fetch('https://service.ralp-ai.site/upload', {
          method: 'POST',
          body: formDataImage
        });

        if (!uploadRes.ok) throw new Error("Upload failed");

        const uploadData = await uploadRes.json();
        setPreviewUrl(`${uploadData.url}?t=${Date.now()}`);
        setCloudinaryUrl(uploadData.url);
      } catch (err) {
        setError(t('cv_upload_error'));
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleOptimizePrompt = async () => {
    if (!prompt.trim()) return;
    const originalPrompt = prompt;
    setOptimizing(true);
    try {
      const response = await fetch('https://service.ralp-ai.site/optimize-veo3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt })
      });
      const data = await response.json();
      if (data.veo3_suggestion) {
        setPrompt(data.veo3_suggestion);
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'bot',
          content: t('cv_optimize_title', { original: originalPrompt, improved: data.veo3_suggestion })
          , animate: true
        }]);
      }
    } catch (err) {
      console.error("Erreur optimisation", err);
    } finally {
      setOptimizing(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if ((!prompt.trim() && !cloudinaryUrl) || uploadingImage || loading || optimizing) return;

    if ((user.tokens || 0) < 10) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        content: t('cv_tokens_insufficient'),
        isError: true,
        animate: true
      }]);
      handleRechargeClick();
      return;
    }

    const userMsg = {
      id: Date.now(),
      type: 'user',
      content: prompt,
      image: previewUrl,
      settings: { aspectRatio, language }
    };
    
    const dataToProcess = {
      prompt,
      image: cloudinaryUrl,
      aspectRatio,
      language
    };
    setPendingData(dataToProcess);

    setMessages(prev => [...prev, userMsg, {
      id: Date.now() + 1,
      type: 'bot',
      content: t('cv_confirm_gen', { prompt }),
      image: cloudinaryUrl,
      isConfirmation: true,
      animate: true
    }]);

    setPrompt('');
    setImageFile(null);
    setPreviewUrl('');
    setCloudinaryUrl('');
  };

  const handleConfirm = async () => {
    if (!pendingData) return;

    setMessages(prev => prev.map(msg => 
      msg.isConfirmation ? { ...msg, isConfirmation: false, content: t('cv_conf_received'), animate: true } : msg
    ));

    setLoading(true);
    const { prompt: apiPrompt, image: apiImage, aspectRatio: apiRatio, language: apiLang } = pendingData;

    try {
      let response;
      if (apiImage) {
        response = await api.post('/video/generate', {
          prompt: apiPrompt,
          duration: '8',
          aspectRatio: apiRatio,
          language: apiLang,
          imageUrl: apiImage
        });
      } else {
        const formData = new FormData();
        formData.append('prompt', apiPrompt);
        formData.append('duration', '8');
        formData.append('aspectRatio', apiRatio);
        formData.append('language', apiLang);
        response = await api.post('/video/generate', formData);
      }

      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        videoUrl: response.data.url,
        content: t('cv_success_gen'),
        animate: true
      }]);

      if (response.data.tokens_remaining !== undefined) {
        const updatedUser = { ...user, tokens: response.data.tokens_remaining };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        type: 'bot',
        content: t('cv_critical_error'),
        isError: true,
        animate: true
      }]);
    } finally {
      setLoading(false);
      setPendingData(null);
    }
  };

  const handleCancel = () => {
    setMessages(prev => prev.map(msg => 
      msg.isConfirmation ? { ...msg, isConfirmation: false, content: t('cv_gen_canceled'), animate: true } : msg
    ));
    setPendingData(null);
  };

  const handleRechargeClick = async () => {
    setPaymentLoading(true);
    try {
      const response = await api.get('/payment/packages');
      setPackages(response.data);
      setShowModal(true);
    } catch (err) {
      alert(t('cv_offer_error'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleBuyPackage = async (packageId) => {
    setPaymentLoading(true);
    try {
      const response = await api.post('/payment/create-session', { packageId, email: user.email });
      if (response.data.url) window.location.href = response.data.url;
    } catch (err) {
      setPaymentLoading(false);
    }
  };

  const handleClearHistory = () => {
    setShowClearHistoryModal(true);
  };

  const confirmClearHistory = () => {
    setMessages([{
      id: 'welcome',
      type: 'bot',
      content: t('welcomeStudio'),
      animate: true
    }]);
    localStorage.removeItem('chat_history');
    setPendingData(null);
    setShowClearHistoryModal(false);
  };

  return (
    <div className="dashboard-wrapper chat-mode-wrapper">
      <style>{`
        @media (max-width: 768px) {
          .chat-input-area {
            position: fixed !important;
            bottom: 80px !important;
            left: 0 !important;
            right: 0 !important;
            padding: 10px 8px !important;
            width: 100% !important;
            box-sizing: border-box !important;
            z-index: 1000 !important;
          }
          .chat-options-bar {
            margin-bottom: 6px !important;
            gap: 5px !important;
            overflow-x: auto;
            padding-bottom: 2px;
          }
          .chat-options-bar::-webkit-scrollbar {
            display: none;
          }
          .chat-option-select, .chat-options-bar button {
            font-size: 11px !important;
            padding: 4px 8px !important;
            height: 28px !important;
            white-space: nowrap;
          }
          .chat-option-badge {
            display: none !important;
          }
          .chat-textarea {
            height: 40px !important;
            min-height: 40px !important;
            padding: 8px 10px !important;
            font-size: 16px !important;
            flex: 1 !important;
          }
          .chat-input-row {
            width: 100% !important;
            gap: 6px !important;
            box-sizing: border-box !important;
          }
          .btn-send-chat, .btn-upload-icon {
            width: 40px !important;
            height: 40px !important;
            min-width: 40px !important;
            flex-shrink: 0 !important;
          }
          .chat-messages {
            padding-bottom: 180px !important;
          }
        }
      `}</style>
      <div className="chat-header-simple glass reveal">
        <div className="chat-header-info">
          <div className="bot-avatar"><Sparkles size={20} color="#fff" /></div>
          <div>
            <h3>{t('studioEngine')}</h3>
            <div className="chat-status">
              <span className="status-dot"></span>
              <small>{t('optimizedBy')} • {user.tokens || 0} {t('tokens')}</small>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="btn-add-token-small" 
            onClick={handleClearHistory} 
            style={{ background: 'rgba(255,255,255,0.1)', color: 'var(--text-muted)' }}
            title={t('clearHistory')}
          >
            <Trash2 size={16} />
          </button>
          <button className="btn-add-token-small" onClick={handleRechargeClick}><Zap size={14} fill="currentColor" /></button>
        </div>
      </div>

      <div className="chat-messages" onScroll={handleScroll}>
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.type} reveal`}>
            <div className="message-avatar">
              {msg.type === 'bot' ? <Bot size={20} /> : <User size={20} />}
            </div>
            <div className={`message-content ${msg.isError ? 'error-msg' : ''} glass`}>
              {msg.image && (
                <div className="message-image-attachment">
                  <img src={msg.image} alt="Reference" />
                </div>
              )}
              {msg.content && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  {msg.isProcessing && (
                    <Loader2 size={18} className="animate-spin" style={{ marginTop: '2px', flexShrink: 0 }} />
                  )}
                  <div style={{ whiteSpace: 'pre-wrap' }}>
                    {msg.type === 'bot' && msg.animate ? (
                      <Typewriter text={msg.content} onComplete={() => handleAnimationComplete(msg.id)} />
                    ) : msg.content}
                  </div>
                </div>
              )}
              {msg.isConfirmation && (
                <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={handleConfirm}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '8px 16px', borderRadius: '8px', border: 'none',
                      background: 'var(--accent)', color: 'white', cursor: 'pointer'
                    }}
                  >
                    <CheckCircle2 size={16} /> {t('confirm')}
                  </button>
                  <button 
                    onClick={handleCancel}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)',
                      background: 'rgba(255,255,255,0.1)', color: 'var(--text-main)', cursor: 'pointer'
                    }}
                  >
                    <X size={16} /> {t('cancel')}
                  </button>
                </div>
              )}
              {msg.videoUrl && (
                <div className="message-video-attachment">
                  <video controls src={msg.videoUrl} autoPlay muted loop playsInline />
                  <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <a href={msg.videoUrl} download={`ralpai-video-${msg.id}.mp4`} className="download-link-apple">
                      <Download size={14} style={{ marginRight: '6px' }} />
                      {t('save')}
                    </a>
                    {!msg.isMerged && (
                      <button 
                        onClick={() => handleExtendVideo(msg.videoUrl)}
                        className="download-link-apple"
                        style={{ 
                          background: 'rgba(255, 255, 255, 0.1)', 
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          cursor: 'pointer',
                          color: 'inherit',
                          fontFamily: 'inherit',
                          fontSize: 'inherit'
                        }}
                      >
                        <Film size={14} style={{ marginRight: '6px' }} />
                        {t('generateSequel')}
                      </button>
                    )}
                  </div>
                </div>
              )}
              {msg.isMergeConfirmation && (
                <div style={{ marginTop: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '15px' }}>
                  <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: 'var(--text-muted)' }}>{t('mergeOrder')}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' }}>
                    {mergeList.map((video, idx) => (
                      <div key={video.id} style={{ 
                        display: 'flex', alignItems: 'center', gap: '10px', 
                        background: 'rgba(255,255,255,0.05)', padding: '8px', borderRadius: '8px' 
                      }}>
                        <span style={{ fontWeight: 'bold', color: 'var(--text-muted)', width: '20px' }}>{idx + 1}</span>
                        <video src={video.videoUrl} controls style={{ width: '100px', height: '60px', objectFit: 'cover', borderRadius: '4px', backgroundColor: '#000' }} />
                        <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                          {t('scene')} {idx + 1}
                        </div>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button onClick={() => moveVideo(idx, 'up')} disabled={idx === 0} style={{ padding: '4px', background: 'none', border: 'none', color: 'var(--text-main)', cursor: 'pointer', opacity: idx === 0 ? 0.3 : 1 }}>
                            <ArrowUp size={14} />
                          </button>
                          <button onClick={() => moveVideo(idx, 'down')} disabled={idx === mergeList.length - 1} style={{ padding: '4px', background: 'none', border: 'none', color: 'var(--text-main)', cursor: 'pointer', opacity: idx === mergeList.length - 1 ? 0.3 : 1 }}>
                            <ArrowDown size={14} />
                          </button>
                          <button onClick={() => removeVideoFromMerge(idx)} style={{ padding: '4px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    {mergeList.length < 2 && <div style={{textAlign: 'center', padding: '10px', color: '#ef4444', fontSize: '0.8rem'}}>{t('selectTwoVideos')}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={executeMerge}
                    disabled={mergeList.length < 2}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '8px 16px', borderRadius: '8px', border: 'none',
                      background: mergeList.length < 2 ? 'gray' : '#ec4899', color: 'white', 
                      cursor: mergeList.length < 2 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <CheckCircle2 size={16} /> {t('merge')}
                  </button>
                  <button 
                    onClick={cancelMerge}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)',
                      background: 'rgba(255,255,255,0.1)', color: 'var(--text-main)', cursor: 'pointer'
                    }}
                  >
                    <X size={16} /> {t('cancel')}
                  </button>
                  </div>
                </div>
              )}
              {msg.settings && (
                <div className="message-meta">
                  <span>{msg.settings.aspectRatio === 'pc' ? <Monitor size={12} /> : <Smartphone size={12} />} {msg.settings.aspectRatio === 'pc' ? '16:9' : '9:16'}</span>
                  <span><Globe size={12} /> {msg.settings.language.toUpperCase()}</span>
                  <span><Clock size={12} /> 8s</span>
                </div>
              )}
            </div>
          </div>
        ))}
        {(loading || optimizing) && (
          <div className="message bot reveal">
            <div className="message-avatar"><Bot size={20} /></div>
            <div className="message-content loading-bubble glass">
              <Loader2 size={18} className="animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area glass reveal">
        {previewUrl && (
          <div className="input-image-preview">
            <img src={previewUrl} alt="Preview" />
            <button onClick={handleRemoveImage} className="btn-remove-img"><X size={12} /></button>
          </div>
        )}

        <div className="chat-options-bar">
          <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className="chat-option-select">
            <option value="pc">{t('landscapeRatio')}</option>
            <option value="mobile">{t('portraitRatio')}</option>
          </select>
          <select value={language} onChange={(e) => setLanguage(e.target.value)} className="chat-option-select">
            <option value="fr">Français (FR)</option>
            <option value="en">English (EN)</option>
            <option value="es">Español (ES)</option>
          </select>
          <button 
            type="button"
            onClick={handleOptimizePrompt}
            disabled={optimizing || !prompt.trim()}
            style={{
              background: '#8A2BE2',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              opacity: (!prompt.trim() || optimizing) ? 0.5 : 1
            }}
          >
            <Zap size={14} fill="currentColor" />
            {optimizing ? t('optimizing') : t('improvePrompt')}
          </button>
          <div className="chat-option-badge">{t('highFidelity')}</div>
          {generatedVideos.length > 1 && (
            <button 
              type="button"
              onClick={handleMergeVideos}
              disabled={merging}
              style={{
                background: '#ec4899',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: merging ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                marginLeft: 'auto',
                opacity: merging ? 0.7 : 1,
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)'
              }}
            >
              {merging ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {t('merging')} {mergeProgress > 0 && `${mergeProgress}%`}
                </>
              ) : (
                <>
                  <Layers size={14} />
                  {t('merge')}
                </>
              )}
            </button>
          )}
        </div>

        <form onSubmit={handleChatSubmit} className="chat-input-row lower-input">
          <label className="btn-upload-icon">
            <ImageIcon size={20} />
            <input type="file" accept=".jpg, .jpeg" onChange={handleImageChange} style={{ display: 'none' }} />
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={uploadingImage ? t('processingImage') : t('explainVision')}
            className="chat-textarea"
            rows="3"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleChatSubmit(e);
              }
            }}
          />
          <button type="submit" className="btn-send-chat" disabled={loading || uploadingImage || optimizing || (!prompt.trim() && !cloudinaryUrl)}>
            <Send size={18} />
          </button>
        </form>
      </div>

      {showModal && (
        <div className="modal-overlay reveal">
          <div className="modal-content glass reveal">
            <button className="close-btn" onClick={() => setShowModal(false)}><X size={20} /></button>
            <div className="modal-header">
              <h2 className="modal-title">{t('propelProjects')}</h2>
              <p className="modal-subtitle">{t('limitlessCreativity')}</p>
            </div>
            <div className="packages-grid">
              {packages.map((pkg, index) => (
                <div key={pkg.id} className={`package-card reveal ${index === 1 ? 'popular' : ''}`}>
                  {index === 1 && <div className="popular-badge"><Trophy size={12} style={{ marginRight: '4px' }} /> {t('recommended')}</div>}
                  <h3 className="package-name">{pkg.name}</h3>
                  <div className="package-price">{(pkg.price / 100).toFixed(2)}€</div>
                  <div className="package-tokens"><Zap size={18} fill="currentColor" /> {pkg.tokens} {t('tokens')}</div>
                  <button className="btn-package-select" onClick={() => handleBuyPackage(pkg.id)} disabled={paymentLoading}>{t('choose')}</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showClearHistoryModal && (
        <div className="modal-overlay reveal" style={{ zIndex: 2000 }}>
          <div className="modal-content glass reveal" style={{ maxWidth: '400px', textAlign: 'center' }}>
            <div className="modal-header" style={{ justifyContent: 'center', marginBottom: '10px' }}>
              <h2 className="modal-title">{t('clearHistoryTitle')}</h2>
            </div>
            <p style={{ marginBottom: '20px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
              {t('clearHistoryConfirm')}<br/><br/>
              <small style={{ opacity: 0.8 }}>{t('clearHistoryNote')}</small>
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="btn-apple-secondary w-full" 
                onClick={() => setShowClearHistoryModal(false)}
                style={{ justifyContent: 'center' }}
              >
                {t('cancel')}
              </button>
              <button 
                className="btn-apple-primary w-full" 
                onClick={confirmClearHistory}
                style={{ justifyContent: 'center', background: '#ef4444', borderColor: '#ef4444' }}
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateVideo;